import * as React from 'react';
import { cn } from '../../lib/utils';
import { FloatingLabel } from '../FloatingLabel';
import { IconX } from '@tabler/icons-react';
import useI18n from '../../hooks/useI18n';
import type { InputLegacyProps } from './types';

/**
 * @deprecated Use the new `Input`, `InputGroup`, and `Field` components instead.
 */
export const InputLegacy = React.forwardRef<HTMLInputElement, InputLegacyProps>(
  (
    {
      type = 'text',
      labelProps,
      placeholder,
      onChange = () => {},
      showClearButton = false,
      clearButtonProps,
      className,
      inputRef,
      value,
      defaultValue,
      error = false,
      id,
      required,
      ...restProps
    },
    ref,
  ) => {
    const { t } = useI18n();
    const [internalValue, setInternalValue] = React.useState(
      defaultValue?.toString() ?? '',
    );
    const isControlled = value !== undefined;
    const displayValue = isControlled ? value : internalValue;
    const reactId = React.useId();
    const formOrGeneratedId = id ?? reactId;

    const truncateFilename = (str: string) => {
      return str.length > 12 ? str.slice(0, 6) + '...' + str.slice(-6) : str;
    };

    const isFileInput = type.toLowerCase() === 'file';
    const floatingLabelProps = labelProps
      ? {
          ...labelProps,
          labelClassName: cn(
            restProps.disabled && 'eye:cursor-not-allowed',
            isFileInput && !restProps.disabled && 'eye:cursor-pointer',
            labelProps.labelClassName,
          ),
        }
      : undefined;

    const inputDefaultClasses = cn(
      'eye:body-m eye:focus:outline-none eye:w-full eye:placeholder:text-grey-placeholder!',
      restProps.disabled && 'eye:text-grey-disabled eye:cursor-not-allowed',
    );
    const canShowClearButton =
      !restProps.disabled && !!displayValue?.toString();

    const clearValue = () => {
      if (isControlled) {
        onChange?.({
          target: { value: '' },
        } as React.ChangeEvent<HTMLInputElement>);
      } else {
        setInternalValue('');
      }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!isControlled) {
        setInternalValue(e.target.value);
      }
      onChange?.(e);
    };

    return (
      <FloatingLabel
        htmlFor={formOrGeneratedId}
        ref={ref}
        required={required}
        disabled={restProps.disabled}
        className={`${error && 'eye:border-red-100 eye:hover:border-red-100'} ${className}`}
        labelClassName={isFileInput ? 'eye:cursor-pointer' : ''}
        {...floatingLabelProps}
      >
        {isFileInput ? (
          <>
            <input
              id={formOrGeneratedId}
              {...restProps}
              ref={inputRef}
              className='eye:hidden'
              onChange={(e) => {
                if (!isControlled) {
                  setInternalValue(e.target.files?.[0]?.name ?? '');
                }
                onChange?.(e);
              }}
              type={type}
            />
            <span className={cn(inputDefaultClasses, className)}>
              {displayValue
                ? truncateFilename(displayValue.toString())
                : placeholder}
            </span>
          </>
        ) : (
          <input
            id={formOrGeneratedId}
            ref={inputRef}
            className={cn(inputDefaultClasses)}
            type={type}
            placeholder={placeholder}
            value={displayValue ?? ''}
            onChange={handleChange}
            {...restProps}
          />
        )}
        {showClearButton && (
          // Named and keyboard-operable: a bare span with an icon is invisible
          // to assistive technology and unreachable without a mouse. Rendered as
          // a span with role="button" rather than a real <button>, because the
          // FloatingLabel wrapper puts children inside its <label>.
          <span
            role='button'
            tabIndex={canShowClearButton ? 0 : -1}
            aria-label={t('resetInput')}
            aria-hidden={!canShowClearButton}
            className={cn(
              'eye:cursor-pointer eye:outline-offset-3 eye:outline-black',
              'eye:focus-visible:outline-2',
              !canShowClearButton && 'eye:invisible',
            )}
            {...clearButtonProps}
            onClick={clearValue}
            onKeyDown={(event) => {
              if (event.key !== 'Enter' && event.key !== ' ') return;
              event.preventDefault();
              clearValue();
            }}
          >
            <IconX aria-hidden='true' />
          </span>
        )}
      </FloatingLabel>
    );
  },
);

InputLegacy.displayName = 'Input';
